package fr.frux.shared.domain.model

/**
 * Forme de la plante — axe de filtre de second niveau. Distinct de la partie recoltee, qui est
 * l'axe principal de decouverte : un sureau est un arbuste dont on recolte fleurs ET baies, a deux
 * saisons differentes (cf. spec du referentiel).
 */
enum class PlantForm { ARBRE, ARBUSTE, HERBACEE, LIANE }

/**
 * Espece telle qu'elle est portee par un arbre. Cette forme reduite suffit a afficher et filtrer
 * un arbre sans imposer le chargement prealable du referentiel complet.
 */
data class SpeciesRef(
    val id: Long,
    val commonNameFr: String,
    val scientificName: String,
    val form: PlantForm,
) {
    /** Accents et casse ignorés : « chataignier » trouve le châtaignier. */
    fun matches(query: String): Boolean = commonNameFr.containsFolded(query) || scientificName.containsFolded(query)
}

/** Partie recoltee — axe principal de decouverte : on sort ramasser des chataignes. */
enum class HarvestPart { FRUIT, FLEUR, FEUILLE, NOIX, RACINE }

/**
 * Toxicite crue de la partie recoltee (ADR 0015) : attribut de la partie, pas du taxon -- les
 * fleurs de sureau se mangent crues, ses baies non. `EDIBLE_RAW` est le defaut assume : une entree
 * qui ne documente rien n'est jamais presentee comme dangereuse.
 */
enum class RawEdibility { EDIBLE_RAW, REQUIRES_PREPARATION, TOXIC_RAW }

/**
 * Fenetre de recolte nationale d'une partie donnee.
 *
 * `monthEnd` peut etre inferieur a `monthStart` : la fenetre chevauche alors l'annee civile
 * (cynorhodon d'octobre a fevrier). D'ou [isInSeason] plutot qu'un `in monthStart..monthEnd`
 * ecrit sur chaque site d'appel — c'est le bug que ce type existe pour rendre impossible.
 */
data class HarvestWindow(
    val part: HarvestPart,
    val monthStart: Int,
    val monthEnd: Int,
    val notes: String? = null,
    val rawEdibility: RawEdibility = RawEdibility.EDIBLE_RAW,
) {
    fun isInSeason(month: Int): Boolean =
        if (monthStart <= monthEnd) {
            month in monthStart..monthEnd
        } else {
            month >= monthStart || month <= monthEnd
        }
}

/** Gravite d'un sosie dangereux (ADR 0015). `LETHAL` gouverne le bandeau rouge de la fiche arbre. */
enum class LookalikeSeverity { TOXIC, LETHAL }

/**
 * Sosie dangereux d'une espece du catalogue (ADR 0015). Jamais une entree du referentiel : le
 * sosie n'est pas recoltable et n'entre pas au catalogue -- seuls son nom, sa gravite et une note
 * d'identification redigee cote serveur sont portes ici.
 */
data class SpeciesLookalike(
    val scientificName: String,
    val commonNameFr: String,
    val severity: LookalikeSeverity,
    val distinguishingNote: String,
    val sourceUrl: String,
)

/**
 * Photo d'illustration de l'espece (#389), jamais une photo d'un arbre. Deja resolue par le
 * serveur en cascade (propre a l'espece, sinon celle de son noeud `rollupId`, sinon absente) :
 * aucune resolution a faire cote client.
 */
data class SpeciesPhoto(
    val url: String,
    val source: String,
    val sourceUrl: String,
    val license: String,
    val author: String? = null,
)

/**
 * Portee d'une espece : [TREE] pour les plantes qui se cartographient comme arbre, [SITE_CROP] pour
 * les cultures de potager reservees aux plantations de lieu (ADR 0036).
 */
enum class SpeciesScope { TREE, SITE_CROP }

/**
 * Entree du referentiel.
 *
 * [rollupId] est la cle d'agregation : une VARIETE remonte a son espece, ce qui fait qu'une
 * recherche « Pommier » compte aussi les arbres taggues « Golden ». Le serveur applique la meme
 * cle (`TreeDao.matchingSpeciesIds`, `SpeciesDao.findApproved`) — les deux etages repondent a la
 * meme question.
 *
 * [harvests] est deja resolu par le serveur : une VARIETE se voit servir les recoltes de son noeud
 * de rollup. Aucune resolution a faire ici.
 */
data class Species(
    val id: Long,
    val parentId: Long?,
    val rollupId: Long,
    val scientificName: String,
    val commonNameFr: String,
    val form: PlantForm,
    val selectable: Boolean,
    val harvests: List<HarvestWindow>,
    val lookalikes: List<SpeciesLookalike> = emptyList(),
    val photo: SpeciesPhoto? = null,
    val scope: SpeciesScope = SpeciesScope.TREE,
) {
    /** Vrai si l'une des parties recoltables l'est au mois donne. Le sureau : juin ET septembre. */
    fun isInSeason(month: Int): Boolean = harvests.any { it.isInSeason(month) }

    /** Accents et casse ignorés : « chataignier » trouve le châtaignier. */
    fun matches(query: String): Boolean = commonNameFr.containsFolded(query) || scientificName.containsFolded(query)
}

/**
 * Seul predicat des surfaces arbre (ajout, correction, recherche carte, saison) : une culture de
 * lieu n'en fait jamais partie.
 */
val Species.offeredForTrees: Boolean
    get() = selectable && scope == SpeciesScope.TREE
